import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  ContentCard,
  Drawer,
  LoadingSkeleton,
  Row,
  TruncatedAccount,
  formatNumber,
} from "@narmi/design_system";
import { useSetChatVisibility, catcat as cc, useNotificationContext } from "cerulean";
import { useLocalization } from "@fluent/react";
import { DateTime } from "luxon";
import {
  DisplayStatus,
  showFileImportCancelOption,
} from "byzantine/src/dbbl/businessLogic/entities/fileImportActivity";
import type { FormattedFileImportItem } from "byzantine/src/dbbl/businessLogic/entities/fileImportActivity/types";
import { showAmountStrikeThrough } from "byzantine/src/dbbl/businessLogic/entities/paymentActivity/helpers";
import {
  useFetchBatch,
  useSelectedWireImportBatch,
} from "byzantine/src/dbbl/businessLogic/entities/wireImport";
import Account from "byzantine/src/Account";
import { useUser } from "byzantine/src/dbbl/businessLogic/entities/users/hooks";
import { useAllAccounts } from "byzantine/src/dbbl/businessLogic/entities/accounts";
import { triggerBlobDownload } from "../WireFileImport/helpers";
import { DrawerHeader, DrawerCard } from "../common";
import { PAYMENT_ACTIVITY_ROUTE } from "../routes";
import styles from "../PaymentActivity.module.scss";

export type WireImportDrawerProps = {
  isOpen: boolean;
  item: FormattedFileImportItem | null;
  itemUuid: string;
  handleClose: () => void;
  handleOnNext: () => void;
  handleOnPrev: () => void;
  handleToggleCancelDialog: (
    id: API.BulkPaymentId | API.WireImport.WireImportBatchId | API.DualApprovalId,
    type?: "ach" | "wire"
  ) => void;
};

const WireImportDrawer = ({
  isOpen,
  item,
  itemUuid,
  handleClose,
  handleOnNext,
  handleOnPrev,
  handleToggleCancelDialog,
}: WireImportDrawerProps) => {
  const drawerIdRef = useRef(
    `Drawer-${Math.random().toString(36).slice(2, 11)}`
  );
  useSetChatVisibility(drawerIdRef.current, !isOpen);
  const { l10n } = useLocalization();
  const navigate = useNavigate();
  const { sendNotification } = useNotificationContext();
  const currentUser = useUser();
  const accounts = useAllAccounts();
  const { send: fetchBatch } = useFetchBatch();
  const selectedBatch = useSelectedWireImportBatch();

  const [batch, setBatch] =
    useState<API.WireImport.WireImportBatch | null>(null);

  // Fetch batch data when drawer opens / item changes
  useEffect(() => {
    if (!itemUuid) return;

    setBatch(null);
    fetchBatch(itemUuid).catch(() => undefined);
  }, [itemUuid, fetchBatch]);

  // Sync from Redux when selected batch updates
  useEffect(() => {
    if (selectedBatch && selectedBatch.id === itemUuid) {
      setBatch(selectedBatch);
    }
  }, [selectedBatch, itemUuid]);

  const resolvedAccount = batch
    ? (() => {
        const acct = accounts.find(
          (a) => a.id === batch.from_account
        );
        return acct ? Account.deserialize(acct) : null;
      })()
    : item?.institutionAccount ?? null;

  const displayStatus = item?.displayStatus
    ?? (batch ? DisplayStatus[batch.state] || DisplayStatus.unknown : "");
  const showStrikeThrough = showAmountStrikeThrough(displayStatus);
  const title = item?.title
    ?? batch?.description
    ?? "Wire Import";
  const netTotalDollarsWithSign = item?.netTotalDollarsWithSign
    ?? (batch
      ? formatNumber(batch.total_amount / 100, "currency")
      : "");
  const wireCount = batch?.wire_count ?? item?.paymentCount ?? 0;
  const dualApprovalId = item?.dualApprovalId ?? null;

  const showCancel = item
    ? showFileImportCancelOption(item, {
        currentUserUuid: currentUser?.id,
      })
    : false;
  const handleDownloadFile = async () => {
    if (!batch?.file_name) return;
    try {
      const encodedFileName = batch.file_name
        .split("/")
        .map(encodeURIComponent)
        .join("/");
      const response = await fetch(
        `/v1/wire_import/csv/download/${encodedFileName}`,
        { method: "GET", credentials: "include" },
      );
      if (!response.ok) {
        throw new Error(`Failed to download file: ${response.status}`);
      }
      const blob = await response.blob();
      const displayName = batch.file_name.includes("/")
        ? batch.file_name.split("/").pop() ?? batch.file_name
        : batch.file_name;
      triggerBlobDownload(blob, displayName);
    } catch (error) {
      sendNotification({ type: "negative", text: l10n.getString("wire-review-download-error") });
    }
  };

  const renderTopCard = () => {
    if (!batch) return null;

    const createdDate = batch.created_at
      ? DateTime.fromISO(batch.created_at).toFormat("MMM d, yyyy")
      : "";
    const formattedAmount = formatNumber(
      batch.total_amount / 100,
      "currency"
    );

    return (
      <div className="margin--top--l">
        <ContentCard kind="bordered">
          <h1 className="fontColor--primary">
            <span className={cc({ [styles.strikeThrough]: showStrikeThrough })}>
              {netTotalDollarsWithSign}
            </span>
          </h1>
          <hr className="margin--bottom--l margin--top--l" />
          {/* Sending row */}
          {wireCount > 0 ? (
            <>
              <h5 className={styles.drawerLabelHeading}>
                {l10n.getString("label-sending")}
              </h5>
              <div className="margin--bottom--m">
                {formattedAmount}{" "}
                {l10n.getString("via-payments-cta", {
                  total: wireCount,
                })}
              </div>
            </>
          ) : null}

          {/* Submitted date row */}
        {createdDate ? (
          <>
            <h5 className={styles.drawerLabelHeading}>
              {l10n.getString("label-scheduled-date")}
            </h5>
            <div className="margin--bottom--m">
              {createdDate}
            </div>
          </>
        ) : null}

          {/* Method row */}
          <>
            <h5 className={styles.drawerLabelHeading}>
              {l10n.getString(
                "payment-activity-label-method"
              )}
            </h5>
            <div className="margin--bottom--m">
              {l10n.getString(
                "wire-file-import-method-name"
              )}
            </div>
          </>

          {/* Download file button */}
          {batch.file_name && (
            <>
              <h5 className={cc([styles.drawerLabelHeading, styles.marginBottomNone])}>
                {l10n.getString(
                  "label-file"
                )}
              </h5>
              <div className="margin--bottom--xs">
              <Button
                kind="plain"
                size="s"
                onClick={handleDownloadFile}
                disabled={!batch?.file_name}
                testId="wire-import-download-file"
              >
                <span className="narmi-icon-download" />
                <span className="margin--left--xs">
                  {l10n.getString("label-download-file")}
                </span>
              </Button>
              </div>
            </>
          )}

          {/* Action row */}
            <h5 className={cc([styles.drawerLabelHeading, styles.marginBottomNone])}>
              {l10n.getString(
                "label-file-details"
              )}
            </h5>
            {batch.state === "awaiting_approval" &&
              dualApprovalId && (
                <Button
                  kind="plain"
                  onClick={() =>
                    window.location.assign(
                      `/approvals/pending?dualApprovalId=${dualApprovalId}`
                    )
                  }
                  size="s"
                >
                  {l10n.getString("label-view-approvals")}
                  <span className="narmi-icon-chevron-right" />
                </Button>
              )}
              {batch.state !== "awaiting_approval" &&
                wireCount > 0 && (
                  <Button
                    kind="plain"
                    onClick={() =>
                      navigate({
                        pathname: `/${PAYMENT_ACTIVITY_ROUTE}`,
                        search: `?wire_import_batch=${batch.id}`,
                      })
                    }
                    startIcon="eye"
                  >
                    {l10n.getString("view-payments-cta", {
                      total: wireCount,
                    })}
                  </Button>
                )}
        </ContentCard>
      </div>
    );
  };

  const renderPaymentDetails = () => {
    const rows: Array<
      [string, string | undefined | React.ReactElement]
    > = [];

    if (resolvedAccount) {
      rows.push([
        l10n.getString("label-account"),
        <TruncatedAccount
          key="account"
          name={resolvedAccount.name}
          lastFour={resolvedAccount.getMaskedNumber()}
        />,
      ]);
    }

    if (batch?.note) {
      rows.push([
        l10n.getString("label-internal-note"),
        batch.note,
      ]);
    }

    if (rows.length === 0) return null;

    return (
      <DrawerCard
        title={l10n.getString(
          "wire-payment-details-section-title"
        )}
        rows={rows}
      />
    );
  };

  const renderFooter = () => {
    if (!showCancel || !dualApprovalId) return undefined;
    return (
      <Row justifyContent="end">
        <Row.Item shrink>
          <Button
            kind="primary"
            onClick={() =>
              handleToggleCancelDialog(
                dualApprovalId as API.DualApprovalId,
                "wire"
              )
            }
            label={l10n.getString("label-cancel-payment")}
            testId="cancel-bulk-payment-button"
          />
        </Row.Item>
      </Row>
    );
  };

  return (
    <Drawer
      isOpen={isOpen}
      onUserDismiss={handleClose}
      onNext={handleOnNext}
      onPrev={handleOnPrev}
      depth="524px"
      paddingSize="xl"
      footer={renderFooter()}
    >
      <LoadingSkeleton isLoading={!batch}>
        <DrawerHeader title={title} status={displayStatus} />
        {renderTopCard()}
        {renderPaymentDetails()}
      </LoadingSkeleton>
    </Drawer>
  );
};

export default WireImportDrawer;
